import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { CookieService } from 'ngx-cookie';
import { Subject, filter, map, mergeMap, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth/auth.service';
import { LoginService } from './services/login.service';
import { SEOService } from './services/seo.service';
import { TokenService } from './services/token.service';
import { AppConstants } from './shared/app.constants';

@Component({
  selector: 'ness-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  bannerMessages: string = environment.bannerMessages;
  title: string = 'NESS';
  displayTimeoutDialog: boolean = false;
  idleState = 'Your session is currently active. Session will timeout after 30 minutes of inactivity.';
  lastPing?: Date = null;
  timedOut = false;
  loggedIn: boolean = false;

  private _timeoutWarningRemainingMinutes: number = 0;
  private _timeoutWarningRemainingSeconds: number = 0;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(public router: Router,
    private idle: Idle,
    private keepalive: Keepalive,
    private activatedRoute: ActivatedRoute,
    private loginService: LoginService,
    private authService: AuthService,
    private cookieService: CookieService,
    private seoService: SEOService,
    private tokenService: TokenService
  ) {

    this.loginService.userInfoData$.subscribe(
      (info: ServerModels.UserInfoViewModel) => this.loggedIn = !!info
    );

    // sets an idle timeout
    this.idle.setIdle(AppConstants.appIdleTimeoutInSeconds);
    // sets a timeout period of inactivity after which the user will be considered timed out.
    this.idle.setTimeout(AppConstants.appInactivityTimeoutInSeconds);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => {
      this.idleState = 'Click \'Continue\' to resume your NESS login session.'
      this.idle.watch();
      this.timedOut = false;
      this.reset();
    });

    idle.onTimeout.subscribe(() => {
      this.displayTimeoutDialog = false;
      this.idleState = 'NESS session timed out!';
      this.timedOut = true;
      this.logout();
    });

    idle.onIdleStart.subscribe(() => {
      this.idleState = 'Your NESS login session is inactive. Your session will timeout after 30 minutes of inactivity.';
      this.displayTimeoutDialog = true;
    });

    idle.onTimeoutWarning.subscribe((countdown) => {
      this._timeoutWarningRemainingMinutes = Math.floor(countdown / 60);
      this._timeoutWarningRemainingSeconds = (countdown % 60);
      this.idleState = 'You will be logged out of NESS in ' + this._timeoutWarningRemainingMinutes + ' minutes ' + this._timeoutWarningRemainingSeconds + ' seconds due to inactivity.';
    });

    // sets the ping interval to 15 seconds
    this.keepalive.interval(15);

    this.keepalive.onPing.subscribe(() => this.lastPing = new Date());

    // check if user is logged in
    this.resetIdle();

  }

  ngOnInit(): void {
    this.router.events.pipe(filter((event: NavigationEnd) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route: ActivatedRoute) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route: ActivatedRoute) => route.outlet === 'primary'),
      mergeMap((route: ActivatedRoute) => route.data))
      .subscribe((event: NavigationEnd) => {
        this.seoService.updateTitle(event['title']);
        //Updating Description tag dynamically with title
        this.seoService.updateDescription(event['title'] + event['description'])
      });
    this.tokenService.startRefreshTokenTimer();
  }

  public resetIdle(): void {
    let accessToken: boolean = !!this.cookieService.get(environment.authCookieStorageKey) || !!sessionStorage.getItem(environment.authTokenStorageKey);
    if (accessToken) {
      this.idle.watch();
      this.idleState = 'Started';
      this.timedOut = false;
    } else {
      this.idle.stop();
      this.idleState = 'Stopped';
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public logout() {
    let refreshToken: string = sessionStorage.getItem(environment.authRefreshTokenStorageKey);
    this.loginService.logout(refreshToken).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: () => this.logoutCallback(),
      error: (err: any) => this.logoutCallback()
    });
  }

  private async logoutCallback() {
    this.loginService.nextUserInfo(null);
    this.cookieService.remove(environment.authCookieStorageKey, { domain: environment.authCookieDomainKey });
    this.authService.clearClaims();
    sessionStorage.clear();

    // Set logout sesstion storage for current logged in user.
    // User will not login to NESS automatically.
    // User will not logged out from Okta session.
    sessionStorage.setItem('logout', JSON.stringify(true));

    // the Commented out code will force user to logout from all current okta sessions
    // await this.oktaAuth.logout();

    // Redirect user back to login page
    this.router.navigate(['/login']);
  }

  public reset() {
    this.displayTimeoutDialog = false;
    this._timeoutWarningRemainingMinutes = 0;
    this._timeoutWarningRemainingSeconds = 0;
  }
}
